import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Avatar,
  LinearProgress,
  Chip,
  Checkbox,
  Tooltip,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  GetApp as DownloadIcon,
  CalendarMonth as CalendarIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api, { exportReport } from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material';

const DetailedSkillAnalysisModal = ({ open, onClose, candidates = [], summary = "" }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [jds, setJds] = useState([]);
  const [selectedJd, setSelectedJd] = useState('');
  const [showJdSelector, setShowJdSelector] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  // Fetch JDs only when scheduling starts
  const fetchJds = async () => {
    try {
      const res = await api.get("job-descriptions/list_with_pagination/?page_size=100");
      setJds(res.data.results || []);
    } catch (err) {
      console.error("Failed to fetch JDs", err);
      toast.error("Failed to load Job Descriptions");
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === candidates.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(candidates.map(c => c.id));
    }
  };

  const handleExport = async () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select at least one candidate');
      return;
    }

    setIsExporting(true);
    try {
      let skills = [];
      if (candidates.length > 0 && candidates[0].semantic_trace) {
        skills = candidates[0].semantic_trace.map(t => t.skill.split(' (')[0]);
      } else {
        skills = summary.match(/Candidates: (.*)/)?.[1]?.split(', ') || 
                 summary.match(/for (.*)/)?.[1]?.split(', ') || 
                 [];
      }

      const response = await exportReport(selectedIds, skills);
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Skill_Analysis_Report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  const handleScheduleClick = () => {
    if (selectedIds.length === 0) {
      toast.warning('Please select at least one candidate');
      return;
    }
    fetchJds();
    setShowJdSelector(true);
  };

  const handleConfirmSchedule = async () => {
    if (!selectedJd) {
      toast.warning('Please select a Job Description');
      return;
    }

    setIsScheduling(true);
    try {
      const payload = {
        jd_id: selectedJd,
        candidate_ids: selectedIds,
        focus_skills: [], // Default empty or extract from context
      };
      const res = await api.post('/interviews/bulk-schedule/', payload);
      if (res.data && res.data.task_id) {
        toast.success('Bulk scheduling initiated');
        // Navigate to Bulk Schedule page with task ID to show progress modal
        navigate('/bulk-schedule', { 
          state: { 
            taskId: res.data.task_id 
          } 
        });
      } else {
        toast.error('Failed to start bulk scheduling');
      }
    } catch (err) {
      console.error('Failed to schedule interviews', err);
      toast.error('Error starting bulk scheduling');
    } finally {
      setIsScheduling(false);
      setShowJdSelector(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          bgcolor: '#ffffff', // Light Theme
          backgroundImage: 'none',
          borderRadius: fullScreen ? 0 : 3,
          overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          color: '#1e293b'
        }
      }}
    >
      {/* Shortened Header */}
      <Box
        sx={{
          background: 'linear-gradient(90deg, #a855f7 0%, #6366f1 100%)',
          p: 2.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold" color="white">
            Candidate Skill Analysis
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', letterSpacing: 0.5 }}>
            SEMANTIC MATCHING & COMPETENCY BREAKDOWN
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
        {showJdSelector ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom fontWeight="bold">
              Select Job Description
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Choose which role these {selectedIds.length} candidates are being scheduled for.
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: 400, mx: 'auto', mb: 4 }}>
              <InputLabel>Target Job Description</InputLabel>
              <Select
                value={selectedJd}
                label="Target Job Description"
                onChange={(e) => setSelectedJd(e.target.value)}
              >
                {jds.map(jd => (
                  <MenuItem key={jd.id} value={jd.id}>{jd.title} ({jd.department_name})</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" onClick={() => setShowJdSelector(false)}>Cancel</Button>
              <Button 
                variant="contained" 
                onClick={handleConfirmSchedule}
                disabled={isScheduling || !selectedJd}
                startIcon={isScheduling ? <CircularProgress size={20} color="inherit" /> : <CalendarIcon />}
                sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    color: 'white !important', // Ensure text is white
                    '& .MuiButton-startIcon': { color: 'white' }
                }}
              >
                {isScheduling ? 'Initiating...' : 'Confirm Bulk Schedule'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                    onClick={handleSelectAll} 
                    size="small" 
                    sx={{ color: '#6366f1', textTransform: 'none', fontWeight: 600 }}
                >
                    {selectedIds.length === candidates.length ? 'Deselect All' : 'Select All'}
                </Button>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {candidates.map((candidate, index) => (
                <motion.div
                  key={candidate.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Box
                    onClick={() => handleToggleSelect(candidate.id)}
                    sx={{
                      position: 'relative',
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: '#ffffff',
                      border: `1.5px solid ${selectedIds.includes(candidate.id) ? '#6366f1' : '#e2e8f0'}`,
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      boxShadow: selectedIds.includes(candidate.id) ? '0 10px 15px -3px rgba(99, 102, 241, 0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
                      '&:hover': {
                        borderColor: '#6366f1',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                  >
                    {/* Selection indicator */}
                    <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
                      <Checkbox 
                        checked={selectedIds.includes(candidate.id)}
                        sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#6366f1' } }}
                      />
                    </Box>

                    {/* Candidate Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                      <Avatar 
                        src={candidate.avatar} 
                        sx={{ width: 50, height: 50, border: '2px solid #f1f5f9' }} 
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="#0f172a">
                          {candidate.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {candidate.title} • {candidate.experience_years} Years Exp.
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right', mr: 4 }}>
                        <Typography variant="h5" fontWeight="bold" sx={{ color: '#4f46e5' }}>
                          {candidate.match_percentage}%
                        </Typography>
                        <Chip 
                          label={candidate.match_label} 
                          size="small" 
                          sx={{ 
                            bgcolor: '#eef2ff', 
                            color: '#4f46e5', 
                            fontWeight: 700,
                            fontSize: '0.65rem',
                            height: 20
                          }} 
                        />
                      </Box>
                    </Box>

                    {/* Competency Match */}
                    <Box sx={{ mb: 2.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                          OVERALL COMPETENCY MATCH
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>
                          {candidate.match_percentage > 90 ? 'High Precision' : 'Consistent Data'}
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={candidate.overall_competency_match * 100} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3, 
                          bgcolor: '#f1f5f9',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(90deg, #6366f1 0%, #a855f7 100%)',
                            borderRadius: 3
                          }
                        }} 
                      />
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: fullScreen ? '1fr' : '1.2fr 0.8fr', gap: 3 }}>
                      {/* Semantic Trace */}
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, fontWeight: 600, color: '#64748b' }}>
                          SEMANTIC TRACE
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {candidate.semantic_trace?.map((trace, i) => (
                            <Box key={i} sx={{ bgcolor: '#f8fafc', p: 1, borderRadius: 1.5, border: '1px solid #f1f5f9' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>{trace.skill}</Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#6366f1', fontWeight: 700 }}>{trace.score}%</Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={trace.score} 
                                sx={{ 
                                  height: 4, 
                                  borderRadius: 2, 
                                  bgcolor: '#e2e8f0',
                                  '& .MuiLinearProgress-bar': { bgcolor: '#6366f1' }
                                }} 
                              />
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      {/* Skill Similarities */}
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', mb: 1.5, fontWeight: 600, color: '#64748b' }}>
                          SKILL SIMILARITIES
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                          {candidate.skill_similarities?.map((sim, i) => (
                            <Chip 
                              key={i}
                              label={`${sim.skill} (${sim.similarity}%)`}
                              size="small"
                              sx={{
                                bgcolor: '#ffffff',
                                color: '#64748b',
                                border: '1px solid #e2e8f0',
                                fontSize: '0.65rem',
                                height: 24,
                                '&:hover': { bgcolor: '#f8fafc' }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </>
        )}
      </DialogContent>

      {/* Footer Actions */}
      <DialogActions sx={{ p: 2.5, borderTop: '1px solid #f1f5f9', bgcolor: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: '#6366f1', fontSize: 18 }} />
          <Typography variant="caption" color="text.secondary">
            {selectedIds.length} candidate{selectedIds.length !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
        {!showJdSelector && (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              onClick={handleExport}
              disabled={selectedIds.length === 0 || isExporting}
              startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
              sx={{
                color: '#475569',
                borderColor: '#cbd5e1',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: '#f8fafc'
                }
              }}
            >
              {isExporting ? 'Exporting...' : 'Export Report'}
            </Button>
            <motion.div
              animate={selectedIds.length > 0 ? {
                scale: [1, 1.02, 1],
              } : {}}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <Button
                variant="contained"
                onClick={handleScheduleClick}
                disabled={selectedIds.length === 0 || isScheduling}
                startIcon={<CalendarIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  textTransform: 'none',
                  fontWeight: 'bold',
                  borderRadius: 2,
                  color: 'white !important',
                  boxShadow: '0 4px 10px rgba(99, 102, 241, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
                    boxShadow: '0 6px 15px rgba(99, 102, 241, 0.4)'
                  },
                  '&.Mui-disabled': {
                      background: '#e2e8f0',
                      color: '#94a3b8'
                  }
                }}
              >
                Schedule Bulk Interview
              </Button>
            </motion.div>
          </Box>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DetailedSkillAnalysisModal;
